/**
 * Routes of a multi-route (if/else) decision node, builder side.
 *
 * A decision node can carry N named routes; exactly one fires per pass. Each route's `id` is the
 * SOURCE PORT its edge leaves from, which is what lets the canvas show "or" (edges from different
 * ports) apart from "and" (edges from the same port) without opening anything.
 *
 * Keep in sync with the engine's `apps/server/src/morrow/workflows/workflowRouter.ts` — these
 * shapes are written straight into the saved graph JSON and parsed there. A route whose condition
 * the engine doesn't recognise degrades to the default arm, so an unknown value is survivable,
 * never fatal.
 *
 * See docs/architecture/workflow-branching.md.
 */

/** How a route decides whether it is the one to take. */
export type WfRouteCondition =
  /** Always matches — the else / default arm. */
  | { readonly kind: "always" }
  /** The source list still has an unprocessed item. THE loop condition. */
  | { readonly kind: "hasMoreItems" }
  /** The previous step's text contains / does not contain a marker. */
  | { readonly kind: "contains"; readonly value: string }
  | { readonly kind: "notContains"; readonly value: string }
  /** A judgement — hand this one to the model. */
  | { readonly kind: "ask"; readonly text: string };

export type WfRouteConditionKind = WfRouteCondition["kind"];

export interface WfRoute {
  readonly id: string;
  readonly label?: string;
  readonly when: WfRouteCondition;
}

export const ROUTE_CONDITION_KINDS: ReadonlyArray<WfRouteConditionKind> = [
  "hasMoreItems",
  "contains",
  "notContains",
  "ask",
  "always",
];

/** A fresh condition of the chosen kind, with the value fields a new one needs. */
export function emptyCondition(kind: WfRouteConditionKind): WfRouteCondition {
  switch (kind) {
    case "contains":
      return { kind: "contains", value: "" };
    case "notContains":
      return { kind: "notContains", value: "" };
    case "ask":
      return { kind: "ask", text: "" };
    case "hasMoreItems":
      return { kind: "hasMoreItems" };
    case "always":
      return { kind: "always" };
  }
}

/** The free-text part of a condition, if it has one ("" when it takes no value). */
export function conditionValue(when: WfRouteCondition): string {
  if (when.kind === "contains" || when.kind === "notContains") return when.value;
  if (when.kind === "ask") return when.text;
  return "";
}

/** The same condition with its free-text part replaced. */
export function withConditionValue(when: WfRouteCondition, value: string): WfRouteCondition {
  if (when.kind === "contains") return { kind: "contains", value };
  if (when.kind === "notContains") return { kind: "notContains", value };
  if (when.kind === "ask") return { kind: "ask", text: value };
  return when;
}

/** Port ids must be stable and URL/handle-safe: they end up as React Flow handle ids. */
export function routeIdFromLabel(label: string, taken: ReadonlyArray<string>): string {
  const base =
    label
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 24) || "route";
  if (!taken.includes(base)) return base;
  for (let i = 2; i < 100; i += 1) {
    const candidate = `${base}-${i}`;
    if (!taken.includes(candidate)) return candidate;
  }
  return `${base}-${Date.now()}`;
}

/**
 * The two routes a loop needs, which is what nearly every author wants the moment they add one:
 * keep going while there is work left, otherwise finish. `more` is the arm to drag back upstream.
 */
export function defaultLoopRoutes(moreLabel: string, doneLabel: string): WfRoute[] {
  return [
    { id: "more", label: moreLabel, when: { kind: "hasMoreItems" } },
    { id: "done", label: doneLabel, when: { kind: "always" } },
  ];
}

/** True when this node routes (so it draws one port per route instead of a single output). */
export function hasRoutes(routes: ReadonlyArray<WfRoute> | undefined): boolean {
  return Array.isArray(routes) && routes.length > 0;
}

/** A short human summary of a condition, for the node card. */
export function conditionSummary(
  when: WfRouteCondition,
  labels: Readonly<Record<WfRouteConditionKind, string>>,
): string {
  const value = conditionValue(when);
  return value ? `${labels[when.kind]}: ${value}` : labels[when.kind];
}

/**
 * Every app a step uses.
 *
 * A step used to hold one `appSlug`, but real steps often need two connectors at once ("read the
 * sheet AND post the result"). The list supersedes the single field, which is still read so
 * graphs saved before this keep working. Mirrors `nodeApps` in the engine's `workflowApps.ts`.
 */
export function nodeAppSlugs(data: {
  readonly appSlug?: string;
  readonly appSlugs?: ReadonlyArray<string>;
}): string[] {
  const listed = (data.appSlugs ?? []).map((s) => s.trim()).filter((s) => s.length > 0);
  const single = data.appSlug?.trim();
  return [...new Set(listed.length > 0 ? listed : single ? [single] : [])];
}
